import React from "react";
import { TextField, TextFieldProps, InputAdornment, IconButton } from "@mui/material";
import { useFormContext, FieldValues } from "react-hook-form";
import EmailIcon from "@mui/icons-material/Email";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

/**
 * Generic input component that registers with React Hook Form.
 */

export type AuthTextFieldProps = TextFieldProps & {
    name: string;
};
export function AuthTextField(props: AuthTextFieldProps): JSX.Element {
    const { name, ...rest } = props;
    const {
        register,
        formState: { errors }
    } = useFormContext<FieldValues>();

    return (
        <TextField
            {...register(name)}
            error={Boolean(errors[name])}
            helperText={errors[name]?.message ? String(errors[name]?.message) : undefined}
            {...rest}
        />
    );
}

/**
 * Specialized Email input field with an end adornment.
 * This component preconfigures the "email" field, so you don't have to pass the InputProps every time.
 */
export interface EmailTextFieldProps extends Omit<AuthTextFieldProps, "name"> {}

export function EmailTextField(props: EmailTextFieldProps): JSX.Element {
    return (
        <AuthTextField
            name="email"
            {...props}
            InputProps={{
                ...props.InputProps,
                endAdornment: (
                    <InputAdornment position="end">
                        <IconButton aria-label="email icon" edge="end" disabled>
                            <EmailIcon color="primary" />
                        </IconButton>
                    </InputAdornment>
                )
            }}
        />
    );
}

/**
 * Specialized Password input field with a toggle for visibility.
 * It accepts additional props for controlling the visibility state.
 */
export interface PasswordTextFieldProps extends Omit<AuthTextFieldProps, "name" | "type"> {
    showPassword: boolean;
    onTogglePasswordVisibility: () => void;
    name?: string;
}

export function PasswordTextField(props: PasswordTextFieldProps): JSX.Element {
    const { showPassword, onTogglePasswordVisibility, name = "password", ...rest } = props;
    return (
        <AuthTextField
            name={name}
            type={showPassword ? "text" : "password"}
            {...rest}
            InputProps={{
                ...rest.InputProps,
                endAdornment: (
                    <InputAdornment position="end">
                        <IconButton
                            aria-label="toggle password visibility"
                            onClick={onTogglePasswordVisibility}
                            edge="end"
                        >
                            {showPassword ? <VisibilityOff color="primary" /> : <Visibility color="primary" />}
                        </IconButton>
                    </InputAdornment>
                )
            }}
        />
    );
}
